import React, { FC, useContext, useEffect, useState } from 'react';
import { Box, FormControlLabel, FormGroup, Radio, RadioGroup, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';

interface Props {
  question: any;
  step: number;
  onChange: (
    qid: string,
    val: string,
    i: number,
    p: boolean,
    disableNext: boolean
  ) => Promise<void>;
  value?: string;
  selectedLanguage?: string;
}

const CustomQuestionSingle: FC<Props> = ({ question, step, onChange, value, selectedLanguage }) => {
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation('customSurvey');
  const t = localContext ? localContext.t : globalT;
  const isCustomGate = question?.type === 'CUSTOMGATE';
  const fixedGateOptions = [{ label: 'yes' }, { label: 'no' }] as const;

  const [selected, setSelected] = useState<Number>();
  const [customValue, setCustomValue] = useState<string>('');

  const currentLanguage = selectedLanguage ?? 'de-DE';

  const getQuestionOptions = () => {
    if (isCustomGate) {
      return fixedGateOptions;
    }
    return question.options || [];
  };

  function getTranslatedOption(idx: number) {
    if (isCustomGate) {
      const gateOption = fixedGateOptions[idx];
      return gateOption ? t(gateOption.label) : '';
    }
    if (question?.questionI18n?.options?.[currentLanguage]?.[idx]) {
      return question?.questionI18n?.options?.[currentLanguage]?.[idx];
    }
    return question.options[idx].label;
  }

  useEffect(() => {
    if (!value) {
      return;
    }
    const options = getQuestionOptions();
    let index = 0;
    for (const option of options) {
      if (option.label === value || (isCustomGate && t(option.label) === value)) {
        setSelected(index);
        return;
      }
      index++;
    }
    setSelected(-1);
    setCustomValue(value);
  }, []);
  const optionClick = async (
    e: any,
    index: Number,
    option?: string | { label: string },
    disableNext: boolean = false
  ) => {
    setSelected(index);
    if (e.target.value === undefined) {
      return;
    }

    if (!option) {
      await onChange(question.questionId || '', customValue, step, false, disableNext);
      return;
    }

    if (typeof option === 'string') {
      await onChange(question.questionId || '', option, step, false, disableNext);
      return;
    }

    if (option.label === value) {
      await onChange(question.questionId || '', '', step, false, disableNext);
      setSelected(undefined);
    } else {
      await onChange(question.questionId || '', option.label, step, false, disableNext);
    }
  };

  const updateCustom = (e: any) => {
    if (e.target && e.target.value !== undefined) {
      let value = e.target.value;
      const disableNext = value.length === 0;
      setCustomValue(value);
      optionClick(e, -1, { label: value }, disableNext);
    }
  };

  return (
    <Box className="survey-custom-question survey-custom-question--single">
      <FormGroup className="survey-answer-form-group">
        <RadioGroup className="survey-answer-options survey-answer-options--single">
          {getQuestionOptions().map((option: any, index: any) => (
            <FormControlLabel
              className="survey-answer-option"
              key={question.questionId + '|' + index}
              control={<Radio checked={index === selected} />}
              label={getTranslatedOption(index)}
              value={option.label}
              onClick={(e) => optionClick(e, index, option)}
            />
          ))}
          {question.allowCustom && (
            <FormControlLabel
              className="survey-answer-option survey-answer-option--custom"
              key={question.questionId + '|' + -1}
              control={<Radio checked={-1 === selected} />}
              label={
                <Box
                  className="survey-custom-answer"
                  sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
                >
                  <span className="survey-custom-answer-label">{t('otheroption')}</span>

                  <TextField
                    className="survey-custom-answer-input"
                    disabled={-1 !== selected}
                    required={-1 === selected}
                    value={customValue}
                    onChange={(e) => updateCustom(e)}
                    inputProps={{ maxLength: 40 }}
                  />
                </Box>
              }
              value={'others'}
              onClick={(e) => optionClick(e, -1, undefined, customValue.length === 0)}
            />
          )}
        </RadioGroup>
      </FormGroup>
    </Box>
  );
};

export default CustomQuestionSingle;
